import React from 'react';
import { MdEditNote, MdIosShare, MdSettings } from 'react-icons/md';
import { BsFillQuestionCircleFill } from 'react-icons/bs';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Divider from '../../../../../../legacy/common/components/wysiwyg-form/Divider';
import { FaCrown } from 'react-icons/fa';
import { OutgoingMembershipProductDto } from 'api/dtos/memberships/products';

type MoreOptionsProps = {
  toggleShareModal: () => void;
  toggleMoreOptions: () => void;
  visible: boolean;
  isAdmin: boolean;
  orgId: string;
  organizationMemberships: OutgoingMembershipProductDto[];
};

const MoreOptionsModal: React.FC<MoreOptionsProps> = ({
  toggleShareModal,
  toggleMoreOptions,
  organizationMemberships,
  orgId,
  visible,
  isAdmin
}) => {
  const { t } = useTranslation(['organizations']);
  const router = useRouter();

  const onOptionClick = (action) => () => {
    toggleMoreOptions();
    action();
  };

  const moreOptions = [
    {
      id: 'view-organization',
      condition: () => true,
      onClick:
        ({ orgId, router }) =>
        () => {
          router.push(`/organizations/${orgId}`);
        },
      label: t('moreOptions.orgDetails'),
      icon: <BsFillQuestionCircleFill size={20} />
    },
    {
      id: 'view-organization-membership-checkout',
      condition: () => organizationMemberships?.length > 0,
      onClick:
        ({ orgId, router }) =>
        () => {
          router.push(`/organizations/${orgId}/memberships/checkout`);
        },
      label: t('moreOptions.viewMemberships'),
      icon: <FaCrown size={20} />
    },
    {
      id: 'edit-organization',
      condition: (isAdmin) => isAdmin,
      onClick:
        ({ orgId, router }) =>
        () => {
          router.push(`/organizations/${orgId}/edit`);
        },
      label: t('moreOptions.editDetails'),
      icon: <MdEditNote size={24} />
    },
    {
      id: 'edit-organization-settings',
      condition: (isAdmin) => isAdmin,
      onClick:
        ({ orgId, router }) =>
        () => {
          router.push(`/organizations/${orgId}/edit/settings`);
        },
      label: t('moreOptions.orgSettings'),
      icon: <MdSettings size={24} />
    }
  ];

  return (
    <Modal
      type={ModalType.SMALL}
      onClose={toggleMoreOptions}
      autoHeight
      hideCloseButton
      visible={visible}
    >
      <div className="p-4 flex flex-col">
        <ul>
          {moreOptions.map(
            (option) =>
              option.condition(isAdmin) && (
                <li
                  key={option.id}
                  className="font-normal text-base cursor-pointer py-4 flex align-center gap-2 text-neutral-600"
                  onClick={onOptionClick(
                    option.onClick({
                      orgId,
                      router
                    })
                  )}
                >
                  {option.icon}
                  {option.label}
                </li>
              )
          )}
        </ul>
        <Divider size={4} />
        <div className="flex gap-2 flex-col pb-2 mt-6">
          <Button
            type={ButtonType.Inverted}
            onClick={onOptionClick(toggleShareModal)}
            icon={<MdIosShare size={20} />}
          >
            {t('moreOptions.shareOrganization')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MoreOptionsModal;
